import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { tap } from 'rxjs';
import { UserMaintenanceRoutes } from 'src/app/core/enums/routes.enum';
import { OnSuccess } from 'src/app/shared/decorators/on-success.decorator';
import { redirectAndBypassUnsavedDataCheck } from 'src/app/shared/functions/redirect-and-bypass-unsaved-data-check';
import { IdName } from 'src/app/shared/models/id-name';
import { ICorrectiveActionResponse } from '../models/corrective-action.model';
import { CorrectiveActionApiService } from '../services/corrective-action-api.service';
import { CurrentComponentIdService } from '../services/current-component-id.service';
import {
  CreateCorrectiveAction,
  DeleteCorrectiveAction,
  GetCorrectiveActionById,
  LoadCorrectiveActionsForIO,
  PrintCorrectiveAction,
  UpdateCorrectiveAction,
} from './corrective-action.actions';

export interface CorrectiveActionStateModel {
  correctiveAction: ICorrectiveActionResponse;
  correctiveActionFile: Blob;
  correctiveActionsForIO: IdName[];
}

const CORRECTION_ACTION_STATE_TOKEN = new StateToken<CorrectiveActionStateModel>(
  'correctiveActions'
);

@State<CorrectiveActionStateModel>({
  name: CORRECTION_ACTION_STATE_TOKEN,
  defaults: {
    correctiveAction: <ICorrectiveActionResponse>{},
    correctiveActionFile: <Blob>{},
    correctiveActionsForIO: [],
  },
})
@Injectable()
export class CorrectiveActionState {
  private get currentComponentId(): string {
    return this.componentIdService.get();
  }

  constructor(
    private service: CorrectiveActionApiService,
    private componentIdService: CurrentComponentIdService
  ) {}

  @Action(GetCorrectiveActionById, { cancelUncompleted: true })
  getCorrectiveActionById(
    { getState, setState }: StateContext<CorrectiveActionStateModel>,
    { id }: GetCorrectiveActionById
  ) {
    return this.service.getCorrectiveActionById(id).pipe(
      tap((correctiveAction: ICorrectiveActionResponse) => {
        const state = getState();
        setState({
          ...state,
          correctiveAction,
        });
      })
    );
  }

  @Action(CreateCorrectiveAction, { cancelUncompleted: true })
  @OnSuccess({ message: 'correctiveActionAdded' })
  createCorrectiveAction(
    ctx: StateContext<CorrectiveActionStateModel>,
    { correctiveAction }: CreateCorrectiveAction
  ) {
    return this.service.createCorrectiveAction(correctiveAction).pipe(
      tap(() => {
        redirectAndBypassUnsavedDataCheck(
          `${UserMaintenanceRoutes.Component}/${this.currentComponentId}`
        );
      })
    );
  }

  @Action(UpdateCorrectiveAction, { cancelUncompleted: true })
  @OnSuccess({ message: 'correctiveActionUpdated' })
  updateCorrectiveAction(
    ctx: StateContext<CorrectiveActionStateModel>,
    { correctiveAction }: UpdateCorrectiveAction
  ) {
    return this.service.updateCorrectiveAction(correctiveAction).pipe(
      tap(() => {
        redirectAndBypassUnsavedDataCheck(
          `${UserMaintenanceRoutes.Component}/${this.currentComponentId}`
        );
      })
    );
  }

  @Action(DeleteCorrectiveAction, { cancelUncompleted: true })
  @OnSuccess({ message: 'correctiveActionDeleted' })
  deleteCorrectiveAction(
    ctx: StateContext<CorrectiveActionStateModel>,
    { id }: DeleteCorrectiveAction
  ) {
    return this.service.deleteCorrectiveAction(id).pipe(
      tap(() => {
        redirectAndBypassUnsavedDataCheck(
          `${UserMaintenanceRoutes.Component}/${this.currentComponentId}`
        );
      })
    );
  }

  @Action(PrintCorrectiveAction, { cancelUncompleted: true })
  printCorrectiveAction(
    { getState, setState }: StateContext<CorrectiveActionStateModel>,
    { id }: PrintCorrectiveAction
  ) {
    return this.service.printCorrectiveAction(id).pipe(
      tap((file: Blob) => {
        const state = getState();
        setState({
          ...state,
          correctiveActionFile: file,
        });
      })
    );
  }

  @Action(LoadCorrectiveActionsForIO, { cancelUncompleted: true })
  loadCorrectiveActionsForIO({ getState, setState }: StateContext<CorrectiveActionStateModel>) {
    return this.service.loadCorrectiveActionsForIO().pipe(
      tap((correctiveActionsForIO: IdName[]) => {
        const state = getState();
        setState({
          ...state,
          correctiveActionsForIO,
        });
      })
    );
  }

  @Selector()
  static getCorrectiveActionById(state: CorrectiveActionStateModel) {
    return state.correctiveAction;
  }

  @Selector()
  static getCorrectiveActionFile(state: CorrectiveActionStateModel) {
    return state.correctiveActionFile;
  }

  @Selector()
  static getCorrectiveActionsForIO(state: CorrectiveActionStateModel) {
    return state.correctiveActionsForIO;
  }
}
